// hooks/useDeliveryTime.ts
import { useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { DeliveryType } from '../../../types/enums'
import { selectCurrentCluster } from '../../../../store/slices/currentClusterSlice'
import { DaySchedule, StoreInterface } from '../../../types/types'

interface UseDeliveryTimeProps {
  deliveryType: DeliveryType
  selectedStore?: StoreInterface | null
}

// Конвертация JS дня в твой формат (0=ПН, 6=ВС)
const getDayOfWeek = (date: Date): number => {
  const jsDay = date.getDay() // 0=вс, 1=пн ... 6=сб
  if (jsDay === 0) return 6 // вс
  return jsDay - 1 // пн-сб: 1->0, 2->1, 3->2, 4->3, 5->4, 6->5
}

// Форматирование времени из "HH:MM" в минуты
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

export const useDeliveryTime = ({ deliveryType, selectedStore }: UseDeliveryTimeProps) => {
  const currentCluster = useSelector(selectCurrentCluster)
  const [desiredTime, setDesiredTime] = useState<string | null>(null)
  const [pickerVisible, setPickerVisible] = useState(false)

  // Определяем расписание
  const schedule = useMemo((): DaySchedule[] => {
    if (deliveryType === DeliveryType.PICKUP) {
      return selectedStore?.schedule || []
    }
    return currentCluster?.schedule || []
  }, [deliveryType, selectedStore, currentCluster])

  // Получить расписание для конкретной даты
  const getScheduleForDate = useCallback(
    (date: Date): DaySchedule | undefined => {
      const dayOfWeek = getDayOfWeek(date)
      return schedule.find((s) => s.sequence === dayOfWeek)
    },
    [schedule],
  )

  // Проверка, работает ли в указанную дату и время
  const isTimeAvailable = useCallback(
    (date: Date): boolean => {
      const daySchedule = getScheduleForDate(date)
      if (!daySchedule || daySchedule.dayOff) return false

      const timeInMinutes = date.getHours() * 60 + date.getMinutes()
      const fromMinutes = timeToMinutes(daySchedule.from)
      const toMinutes = timeToMinutes(daySchedule.to)

      let available: boolean
      
      // Если время работы переходит через полночь (to < from)
      if (toMinutes < fromMinutes) {
        available = timeInMinutes >= fromMinutes || timeInMinutes <= toMinutes
      } else {
        available = timeInMinutes >= fromMinutes && timeInMinutes <= toMinutes
      }

      return available
    },
    [getScheduleForDate],
  )

  // Получить минимальную дату (сейчас + 30 минут, с учетом расписания)
  const minimumDate = useMemo((): Date => {
    const now = new Date()
    const minTime = new Date(now.getTime() + 30 * 60 * 1000)

    // Проверяем, попадает ли minTime в рабочее время
    if (isTimeAvailable(minTime)) {
      return minTime
    }

    // Если не попадает, ищем ближайшее рабочее время
    let nextDay = new Date(minTime)
    nextDay.setHours(0, 0, 0, 0)

    for (let i = 0; i < 14; i++) {
      const testDate = new Date(nextDay)
      testDate.setDate(nextDay.getDate() + i)

      const daySchedule = getScheduleForDate(testDate)
      if (daySchedule && !daySchedule.dayOff) {
        const [hours, minutes] = daySchedule.from.split(':').map(Number)
        testDate.setHours(hours, minutes, 0, 0)

        if (testDate < now) {
          continue
        }

        return testDate
      }
    }

    return minTime
  }, [isTimeAvailable, getScheduleForDate])

  // Текст для отображения
  const displayText = desiredTime
    ? new Date(desiredTime).toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'в ближайшее время'

  const openPicker = useCallback(() => {
    setPickerVisible(true)
  }, [])

  const closePicker = useCallback(() => {
    setPickerVisible(false)
  }, [])

  const handleTimeSelect = useCallback(
    (date?: Date) => {
      setPickerVisible(false)
      if (date) {
        if (isTimeAvailable(date)) {
          setDesiredTime(date.toISOString())
        }
      }
    },
    [isTimeAvailable],
  )

  const clearTime = useCallback(() => {
    setDesiredTime(null)
  }, [])

  return {
    desiredTime,
    setDesiredTime,
    displayText,
    pickerVisible,
    openPicker,
    closePicker,
    handleTimeSelect,
    clearTime,
    minimumDate,
    isTimeAvailable,
    schedule,
  }
}